import { toast } from 'sonner'
import type { RolPartener, TipCampanie } from '@b4l/shared'

/**
 * Ce e comun intre lista de activitati si pagina unei activitati: forma randului, culorile de
 * status, formatarea datelor si citirea erorilor de la API.
 *
 * Statea in pagina de lista, fiindca acolo era singurul ecran. Odata cu pagina evenimentului au
 * devenit doua, iar o copie a lor s-ar fi desprins de original la prima schimbare.
 */

/** Un partener legat de activitate — din tabela comună de parteneri, cu rolul lui. */
export interface PartenerCampanie {
  id: number
  rol: RolPartener
  nota: string | null
  partener: {
    id: number
    denumire: string
    codFiscal: string | null
    localitate: string | null
    judet: string | null
    eaStare: string
    esteClient: boolean
    esteFurnizor: boolean
  }
}

/** Numele și cheia de filtru (`?partener=`) ale unui partener legat. */
export function despre(p: PartenerCampanie): { nume: string; cheie: string } {
  return { nume: p.partener.denumire, cheie: String(p.partener.id) }
}

export interface Campanie {
  id: number
  cod: string
  tip: TipCampanie
  titlu: string
  tema: string | null
  dataStart: string
  dataFinal: string | null
  status: string
  responsabilId: number | null
  responsabil: { id: number; email: string } | null
  bugetAlocat: string | null
  observatii: string | null
  tipEveniment: string | null
  oras: string | null
  locatie: string | null
  organizator: string | null
  sursa: string | null
  contactNume: string | null
  contactEmail: string | null
  contactTelefon: string | null
  canaleSociale: string | null
  ceAmAgreat: string | null
  taxaParticipare: string | null
  taxaMoneda: string | null
  participantiEstimati: number | null
  generatAutomat: boolean
  /** Partenerii, cu rol (15.10.2026). Vin și în listă, și pe pagina activității. */
  parteneri?: PartenerCampanie[]
  /** Echipa la eveniment, pe scurt (24.10.2026); lipseste pentru cine citeste prin Sampling. */
  echipa?: { userId: number; user: { name: string }; zile: { zi: string }[] }[]
  /** Cerere de marfă aprobată de peste o săptămână din care n-a plecat toată marfa. */
  cerereUitata?: boolean
  /** Doar în listă: totalul din fila Costuri. */
  costTotal?: number
  /** Doar în listă: din facturile de furnizor din EA. `null` = EA n-a răspuns. */
  plata?: 'platit' | 'in_asteptare' | 'inaplicabil' | null
  /** Doar în listă: de ce are plata starea asta. */
  plataMotiv?: string | null
  /** Doar în listă: cerere de marfă aprobată sau marfă ieșită pe document. */
  areMostre?: boolean
  /** Are o cerere de marfă „Pentru un partener” / „Pentru un colaborator”. */
  cuPartener?: boolean
  cuColaborator?: boolean
  /** Doar în listă: comandat sau contractat, nefacturat — „Angajat" din fila Costuri. */
  angajat?: number
  /** Doar în listă: câte cereri de marfă așteaptă un admin. */
  cereriDeAprobat?: number
  _count?: { actiuniSampling: number }
}

export interface UtilizatorScurt {
  id: number
  name?: string | null
  email: string
}

export const CULOARE_STARE: Record<string, string> = {
  planificata: 'bg-b4l-bg text-b4l-text-muted',
  oportunitate: 'bg-b4l-bg text-b4l-text-muted',
  agreata: 'bg-b4l-blue-light text-b4l-blue-dark',
  in_evaluare: 'bg-b4l-amber-light text-b4l-amber-dark',
  confirmat: 'bg-b4l-blue-light text-b4l-blue-dark',
  activa: 'bg-b4l-green-light text-b4l-green-dark',
  activ: 'bg-b4l-green-light text-b4l-green-dark',
  incheiata: 'bg-b4l-purple-light text-b4l-purple-dark',
  participat: 'bg-b4l-purple-light text-b4l-purple-dark',
  inchis: 'bg-b4l-purple-light text-b4l-purple-dark',
  anulata: 'bg-b4l-red-light text-b4l-red-dark',
  anulat: 'bg-b4l-red-light text-b4l-red-dark',
}

// Sursele vin din `@b4l/shared`: API-ul valideaza aceeasi lista, iar doua copii ale ei ar

// divergea la prima schimbare — cum s-a si intamplat cand s-a cerut „social media".

export function zi(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('ro-RO') : '—'
}

export function perioada(c: Campanie): string {
  return c.dataFinal ? `${zi(c.dataStart)} – ${zi(c.dataFinal)}` : zi(c.dataStart)
}

/** Numele câmpului așa cum scrie pe ecran — mesajul serverului vine pe cheia din cod. */
const ETICHETE_CAMP: Record<string, string> = {
  titlu: 'Titlu',
  tema: 'Temă',
  dataStart: 'Început',
  dataFinal: 'Sfârșit',
  status: 'Status',
  responsabilId: 'Responsabil',
  bugetAlocat: 'Buget alocat',
  observatii: 'Observații',
  tipEveniment: 'Tip eveniment',
  oras: 'Oraș',
  locatie: 'Locație',
  organizator: 'Organizator',
  sursa: 'Sursă',
  contactNume: 'Persoană de contact',
  contactEmail: 'Email contact',
  contactTelefon: 'Telefon contact',
  canaleSociale: 'Canale unde se aplică înțelegerea',
  ceAmAgreat: 'Ce am agreat să le dăm',
  taxaParticipare: 'Taxă / pachet',
  taxaMoneda: 'Monedă',
  participantiEstimati: 'Participanți estimați',
}

/*
  La o eroare de validare, `message` e mereu acelasi text generic — „Invalid request payload" —
  iar motivul adevarat sta pe camp, in `fields`. Ecranul il arunca, asa ca cine completa gresit
  un camp vedea doar formula aia in engleza si nu avea de unde sti ce sa corecteze
  (semnalat 21.09.2026, la crearea unui eveniment).
*/
export function eroare(err: unknown): void {
  const detalii = (
    err as {
      response?: { data?: { error?: { message?: string; fields?: Record<string, string[]> } } }
    }
  )?.response?.data?.error
  const [camp, mesaje] = Object.entries(detalii?.fields ?? {})[0] ?? []
  const peCamp = mesaje?.[0]
  if (peCamp && camp) {
    toast.error(`${ETICHETE_CAMP[camp] ?? camp}: ${peCamp}`)
    return
  }
  toast.error(peCamp ?? detalii?.message ?? 'A apărut o eroare')
}

/**
 * Cererea a fost refuzata pentru drepturi (403). Filele cu acțiuni, predări și transferuri cer
 * dreptul de sampling; cine vede doar evenimentele primea „Nicio acțiune" — fals, datele există,
 * doar că nu le poate vedea (gasit 15.10.2026).
 */
export function faraAcces(err: unknown): boolean {
  return (err as { response?: { status?: number } } | null)?.response?.status === 403
}

export const MESAJ_FARA_SAMPLING =
  'N-ai acces la Sampling (acțiuni, predări, transferuri). Dacă ai nevoie, cere-l unui admin.'
